/**
 * useJournalBlocks Hook
 * React Query hooks for managing journal blocks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCallback, useRef } from 'react';
import {
  getJournalBlocks,
  createJournalBlock,
  updateJournalBlock,
  updateBlockContent,
  deleteJournalBlock,
  reorderBlocks,
} from '@/services/journalBlockService';
import type { CreateBlockInput, UpdateBlockInput } from '@/types/journal';

/**
 * Fetch all blocks for a journal
 */
export function useJournalBlocks(journalId: string | undefined) {
  return useQuery({
    queryKey: ['journal-blocks', journalId],
    queryFn: async () => {
      if (!journalId) throw new Error('Journal ID is required');
      return getJournalBlocks(journalId);
    },
    enabled: !!journalId,
  });
}

/**
 * Create a new block
 */
export function useCreateBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createJournalBlock,
    onSuccess: (newBlock) => {
      // Invalidate blocks list for this journal
      queryClient.invalidateQueries({ queryKey: ['journal-blocks', newBlock.journal_id] });
      // Also update the journal's updated_at timestamp
      queryClient.invalidateQueries({ queryKey: ['journal', newBlock.journal_id] });
    },
    onError: (error) => {
      console.error('Error creating block:', error);
      toast.error('Failed to create block');
    },
  });
}

/**
 * Update a block
 */
export function useUpdateBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      blockId,
      updates,
    }: {
      blockId: string;
      updates: UpdateBlockInput;
    }) => {
      return updateJournalBlock(blockId, updates);
    },
    onMutate: async ({ blockId, updates }) => {
      // Get journal_id from the block in cache
      const blocksCache: any = queryClient.getQueriesData({ queryKey: ['journal-blocks'] });
      let journalId: string | undefined;

      for (const [key, data] of blocksCache) {
        if (Array.isArray(data)) {
          const block = data.find((b: any) => b.id === blockId);
          if (block) {
            journalId = block.journal_id;
            break;
          }
        }
      }

      if (!journalId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['journal-blocks', journalId] });

      // Snapshot previous value
      const previousBlocks = queryClient.getQueryData(['journal-blocks', journalId]);

      // Optimistically update blocks list
      queryClient.setQueryData(['journal-blocks', journalId], (old: any) => {
        if (!old) return old;
        return old.map((block: any) =>
          block.id === blockId ? { ...block, ...updates } : block
        );
      });

      return { previousBlocks, journalId };
    },
    onError: (error, { blockId }, context) => {
      // Rollback on error
      if (context?.previousBlocks && context?.journalId) {
        queryClient.setQueryData(
          ['journal-blocks', context.journalId],
          context.previousBlocks
        );
      }
      console.error('Error updating block:', error);
      toast.error('Failed to update block');
    },
    onSettled: (data) => {
      if (data) {
        // Always refetch after error or success
        queryClient.invalidateQueries({ queryKey: ['journal-blocks', data.journal_id] });
        queryClient.invalidateQueries({ queryKey: ['journal', data.journal_id] });
      }
    },
  });
}

/**
 * Delete a block
 */
export function useDeleteBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteJournalBlock,
    onMutate: async (blockId) => {
      // Get journal_id from the block in cache
      const blocksCache: any = queryClient.getQueriesData({ queryKey: ['journal-blocks'] });
      let journalId: string | undefined;
      let blockToDelete: any;

      for (const [key, data] of blocksCache) {
        if (Array.isArray(data)) {
          blockToDelete = data.find((b: any) => b.id === blockId);
          if (blockToDelete) {
            journalId = blockToDelete.journal_id;
            break;
          }
        }
      }

      if (!journalId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['journal-blocks', journalId] });

      // Snapshot previous value
      const previousBlocks = queryClient.getQueryData(['journal-blocks', journalId]);

      // Optimistically update blocks list
      queryClient.setQueryData(['journal-blocks', journalId], (old: any) => {
        if (!old) return old;
        return old.filter((block: any) => block.id !== blockId);
      });

      return { previousBlocks, journalId };
    },
    onError: (error, blockId, context) => {
      // Rollback on error
      if (context?.previousBlocks && context?.journalId) {
        queryClient.setQueryData(
          ['journal-blocks', context.journalId],
          context.previousBlocks
        );
      }
      console.error('Error deleting block:', error);
      toast.error('Failed to delete block');
    },
    onSuccess: (_, __, context) => {
      if (context?.journalId) {
        queryClient.invalidateQueries({ queryKey: ['journal-blocks', context.journalId] });
        queryClient.invalidateQueries({ queryKey: ['journal', context.journalId] });
      }
      toast.success('Block deleted');
    },
  });
}

/**
 * Reorder blocks
 * Note: We don't invalidate queries on success because local state
 * already has the optimistic update. Invalidating would cause a flash
 * as the old order briefly appears before the new order is applied.
 */
export function useReorderBlocks() {
  return useMutation({
    mutationFn: reorderBlocks,
    onError: (error) => {
      console.error('Error reordering blocks:', error);
      toast.error('Failed to reorder blocks');
      // Could add rollback logic here if needed
    },
  });
}

/**
 * Debounced content update hook
 * Returns a function that debounces content updates
 */
export function useDebouncedBlockContentUpdate(journalId: string | undefined, delay = 1000) {
  const queryClient = useQueryClient();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const debouncedUpdate = useCallback(
    (blockId: string, content: Record<string, any>) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Optimistically update the UI immediately
      queryClient.setQueryData(['journal-blocks', journalId], (old: any) => {
        if (!old) return old;
        return old.map((block: any) =>
          block.id === blockId ? { ...block, content } : block
        );
      });

      // Debounce the actual database update
      timeoutRef.current = setTimeout(async () => {
        try {
          await updateBlockContent(blockId, content);
          // Invalidate journal to update timestamp
          if (journalId) {
            queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
          }
        } catch (error) {
          console.error('Error updating block content:', error);
          toast.error('Failed to save changes');
          // Revert optimistic update on error
          if (journalId) {
            queryClient.invalidateQueries({ queryKey: ['journal-blocks', journalId] });
          }
        }
      }, delay);
    },
    [journalId, queryClient, delay]
  );

  return debouncedUpdate;
}





